/**
 * The field-settings form model — pure, so `field-settings.test.tsx` can pin
 * the two rules that matter without a DOM:
 *
 * 1. THE PATCH IS A DIFF, NEVER A SNAPSHOT (the CountrySettings rule). The
 *    server treats every supplied field as an instruction; a save that posted
 *    all seven would turn "change one retention number" into a re-assertion
 *    of six values the operator never touched.
 * 2. NUMBERS ARE VALIDATED TO THE SERVER'S OWN BOUNDS BEFORE SENDING — not to
 *    invent a second validator, but because the four bounds are DTO facts
 *    (`PatchFieldConfigDto`: radius 1–50000, session 1–1440, retention
 *    1–3650) and a request that will certainly 400 spends a round-trip to
 *    render the same sentence this can render now. The server stays the
 *    authority; these constants are transcribed and test-pinned, not owned.
 */

import type { FieldConfig, FieldConfigPatch } from './api';

/** What the form edits — numbers as STRINGS while being typed. */
export interface FieldConfigForm {
  attendance_enabled: boolean;
  visits_enabled: boolean;
  live_sessions_enabled: boolean;
  arrival_agree_radius_m: string;
  live_max_minutes: string;
  retention_days_stamps: string;
  retention_days_trail: string;
}

export type NumericField =
  | 'arrival_agree_radius_m'
  | 'live_max_minutes'
  | 'retention_days_stamps'
  | 'retention_days_trail';

/** Transcribed from `PatchFieldConfigDto` — the server's own floors/ceilings. */
export const FIELD_BOUNDS: Readonly<Record<NumericField, { min: number; max: number }>> = {
  arrival_agree_radius_m: { min: 1, max: 50000 },
  live_max_minutes: { min: 1, max: 1440 },
  retention_days_stamps: { min: 1, max: 3650 },
  retention_days_trail: { min: 1, max: 3650 },
};

export function formOf(config: FieldConfig): FieldConfigForm {
  return {
    attendance_enabled: config.attendance_enabled,
    visits_enabled: config.visits_enabled,
    live_sessions_enabled: config.live_sessions_enabled,
    arrival_agree_radius_m: String(config.arrival_agree_radius_m),
    live_max_minutes: String(config.live_max_minutes),
    retention_days_stamps: String(config.retention_days_stamps),
    retention_days_trail: String(config.retention_days_trail),
  };
}

/**
 * A typed number field's verdict: the integer, or the sentence to show under
 * the field. The floor sentence states the design's own rule — "keep nothing"
 * is spelled by disabling the feature, not by retention 0.
 */
export function parseNumeric(
  field: NumericField,
  raw: string,
): { value: number } | { error: string } {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) {
    return { error: 'Enter a whole number.' };
  }
  const value = Number(trimmed);
  const bounds = FIELD_BOUNDS[field];
  if (value < bounds.min) {
    return {
      error:
        field === 'retention_days_stamps' || field === 'retention_days_trail'
          ? `At least ${String(bounds.min)} — "keep nothing" is spelled by disabling the feature, not by a zero window.`
          : `At least ${String(bounds.min)}.`,
    };
  }
  if (value > bounds.max) {
    return { error: `At most ${String(bounds.max)}.` };
  }
  return { value };
}

/**
 * Has the operator touched ANYTHING — including a field currently holding an
 * invalid number? String comparison, deliberately: an out-of-bounds draft IS
 * a change (the Save button must stay live so submitting can EXPLAIN the
 * refusal), it is just not a SENDABLE one. Caught by `field-settings.test.tsx`
 * as a live defect: gating Save on `changedFields !== null` alone rendered
 * "Nothing has been changed" over a field holding a refused `0`.
 */
export function isDirty(original: FieldConfigForm, draft: FieldConfigForm): boolean {
  if (
    draft.attendance_enabled !== original.attendance_enabled ||
    draft.visits_enabled !== original.visits_enabled ||
    draft.live_sessions_enabled !== original.live_sessions_enabled
  ) {
    return true;
  }
  return (Object.keys(FIELD_BOUNDS) as NumericField[]).some(
    (field) => draft[field].trim() !== original[field].trim(),
  );
}

/**
 * The diff: only fields whose value differs from the loaded row. `null` when
 * nothing SENDABLE changed. Numeric fields that do not parse are excluded —
 * `validateForm` runs first and a form with errors is never submitted, so a
 * non-null return here is always a fully-valid patch.
 */
export function changedFields(
  original: FieldConfigForm,
  draft: FieldConfigForm,
): FieldConfigPatch | null {
  const patch: FieldConfigPatch = {};
  if (draft.attendance_enabled !== original.attendance_enabled) {
    patch.attendance_enabled = draft.attendance_enabled;
  }
  if (draft.visits_enabled !== original.visits_enabled) {
    patch.visits_enabled = draft.visits_enabled;
  }
  if (draft.live_sessions_enabled !== original.live_sessions_enabled) {
    patch.live_sessions_enabled = draft.live_sessions_enabled;
  }
  for (const field of Object.keys(FIELD_BOUNDS) as NumericField[]) {
    if (draft[field].trim() !== original[field].trim()) {
      const parsed = parseNumeric(field, draft[field]);
      if ('value' in parsed) patch[field] = parsed.value;
    }
  }
  return Object.keys(patch).length === 0 ? null : patch;
}

/** Every numeric field's verdict; empty map = the form may be sent. */
export function validateForm(
  original: FieldConfigForm,
  draft: FieldConfigForm,
): Partial<Record<NumericField, string>> {
  const errors: Partial<Record<NumericField, string>> = {};
  for (const field of Object.keys(FIELD_BOUNDS) as NumericField[]) {
    // Only CHANGED fields are judged: an untouched value is the server's own,
    // and refusing to save because of it would block the one edit being made.
    if (draft[field].trim() === original[field].trim()) continue;
    const parsed = parseNumeric(field, draft[field]);
    if ('error' in parsed) errors[field] = parsed.error;
  }
  return errors;
}
